import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { CheckCircleFilled } from "components/icons/CheckCircleFilled";
import { PlusIcon } from "components/icons/PlusIcon";
import { SOMETHING_WENT_WRONG } from "constants/apiCallError";
import { ADMIN_APPROVED, INITIATED } from "constants/groupRequest";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { ReactNode, useState } from "react";
import {
  DomainResponse,
  useGetDomainRecognizationQuery,
  useJoinCompanyGroupMutation,
  useJoinServiceTeamMutation,
} from "store/apis/serviceTeam";
import { ConfirmEntityStep } from "./ConfirmEntityStep";

type DomainRecognizeProps = {
  onComplete: (summary: ReactNode) => void;
  onError: () => void;
};

type JoinRowProps = {
  group: DomainResponse;
};

const REQUESTED_STATUSES = [INITIATED, ADMIN_APPROVED];

const JoinRow = ({ group }: JoinRowProps) => {
  const { alertToast, successToast } = useToast();
  const { isUserTypeForeignCA } = useRoleBasedView();
  const [joinServiceTeam, { isLoading: isJoiningTeam }] =
    useJoinServiceTeamMutation();
  const [joinCompanyGroup, { isLoading: isJoiningGroup }] =
    useJoinCompanyGroupMutation();

  const isLoading = isJoiningTeam || isJoiningGroup;

  const isRequested = REQUESTED_STATUSES.includes(group.status);

  const handleRequest = async () => {
    try {
      if (isUserTypeForeignCA) {
        await joinServiceTeam({ groupId: group.uuid }).unwrap();
      } else {
        await joinCompanyGroup({ groupId: group.uuid }).unwrap();
      }
      successToast({ message: `Requested to join ${group.name}` });
    } catch (error) {
      const message =
        (error as { data?: { error?: { message?: string } } })?.data?.error
          ?.message ?? SOMETHING_WENT_WRONG;
      alertToast({ message });
    }
  };

  return (
    <div className="t-flex t-items-center t-justify-between t-rounded-lg t-border t-border-solid t-border-neutral-10 t-px-4 t-py-3">
      <span className="t-text-body t-text-text-60">{group.name}</span>
      {isRequested ? (
        <Button
          customType="secondary"
          size="small"
          type="button"
          disabled
          leftIcon={<CheckCircleFilled className="t-text-green-80" />}
        >
          Requested
        </Button>
      ) : (
        <Button
          customType="primary_outlined"
          size="small"
          type="button"
          isLoading={isLoading}
          onClick={handleRequest}
        >
          Request to join
        </Button>
      )}
    </div>
  );
};

export const DomainRecognize = ({
  onComplete,
  onError,
}: DomainRecognizeProps) => {
  const {
    data: groups,
    isLoading,
    isSuccess,
    isError,
  } = useGetDomainRecognizationQuery();

  const [showCreateNew, setShowCreateNew] = useState(false);

  const handleCreateNew = () => setShowCreateNew(true);
  const handleJoinExisting = () => setShowCreateNew(false);

  const hasNoBrands = (groups?.length ?? 0) === 0;
  const createEntity = (
    <ConfirmEntityStep
      onComplete={onComplete}
      onError={onError}
      onJoinExisting={hasNoBrands ? undefined : handleJoinExisting}
    />
  );

  return (
    <Async.Root
      isLoading={isLoading}
      isSuccess={isSuccess}
      isError={isError}
      isEmpty={hasNoBrands || showCreateNew}
    >
      <Async.Empty>{createEntity}</Async.Empty>
      <Async.ErrorHandler>{createEntity}</Async.ErrorHandler>
      <Async.Success>
        <div className="t-flex t-flex-col t-gap-4">
          <p className="t-m-0 t-text-body-sm t-text-text-30">
            We found brand(s) linked to this email domain. You can request
            access to join your team.
          </p>

          <div className="t-flex t-flex-col t-gap-3">
            {groups?.map((group) => (
              <JoinRow key={group.uuid} group={group} />
            ))}
          </div>

          <div className="t-flex t-items-center t-gap-2">
            <div className="t-h-px t-flex-1 t-bg-neutral-10" />
            <span className="t-text-body-sm t-text-text-30">or</span>
            <div className="t-h-px t-flex-1 t-bg-neutral-10" />
          </div>

          <Button
            block
            customType="secondary"
            type="button"
            leftIcon={<PlusIcon />}
            className="t-border-dashed"
            onClick={handleCreateNew}
          >
            Create new
          </Button>
        </div>
      </Async.Success>
    </Async.Root>
  );
};
